import { BaseViewStore } from 'core/stores';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import _ from 'lodash';
import { BillingService } from 'common/data';
import { notificationError } from 'core/utils';

// What both Billing pages share: every unbilled package, loaded from the database so everyone sees the same work
// (Reload picks up other people's changes). Subclasses call refresh() at the end of their constructor and hook into
// loading through beforeLoadMatches and onMatchesLoaded.
class BillingViewStore extends BaseViewStore {
	@observable isRunning = false;
	@observable currentStep = null;
	// .ref: thousands of rows that are only ever replaced, never edited, so MobX needn't wrap each one.
	@observable.ref matches = null;

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.billingService = new BillingService(rootStore.app.baasic.apiClient);
	}

	@computed get matchedPackages() {
		return _.filter(this.matches, x => !x.isNotBillable && isStrongMatch(x));
	}

	@computed get notBillablePackages() {
		return _.filter(this.matches, 'isNotBillable');
	}

	@computed get unmatchedPackages() {
		return _.filter(this.matches, x => !x.isNotBillable && !isStrongMatch(x));
	}

	@action.bound
	refresh() {
		return this.updateMatches('BILLING.STEPLOADMATCHES', () => Promise.resolve());
	}

	// Runs a change to the stored matches, then reloads them.
	@action.bound
	async updateMatches(step, change) {
		this.isRunning = true;
		this.setStep(step);
		try {
			await change();
			await this.loadMatches();
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		} finally {
			runInAction(() => {
				this.isRunning = false;
				this.currentStep = null;
			});
		}
	}

	async loadMatches() {
		this.setStep('BILLING.STEPLOADMATCHES');
		await this.beforeLoadMatches();
		const response = await this.billingService.findMatches();
		runInAction(() => {
			this.matches = response.data;
			this.onMatchesLoaded();
		});
	}

	async beforeLoadMatches() {}

	onMatchesLoaded() {}

	@action.bound
	setStep(step) {
		this.currentStep = step;
	}
}

// Matched once the package has a carrier account: from its tracking number, a strong name match to a seller with a
// single account of that type, or a manual assignment. A seller without a carrier is only a suggestion.
function isStrongMatch(match) {
	return match.matchedBy === 'TrackingNumber' || match.carrierId != null;
}

export default BillingViewStore;
